import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, Phone, Mail, User, MessageSquare, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { submitLead, getGeoLocation, buildLeadPayload, getCountryCodeFromDialCode } from '../services/api';
import { validateEmail, validatePhone, validateName, validateRequired } from '../utils/validation';

const ContactModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateField = (field, value) => {
    let validation = { isValid: true, error: '' };

    switch (field) {
      case 'name':
        validation = validateName(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'phone':
        validation = validatePhone(value, formData.countryCode);
        break;
      case 'description':
        validation = validateRequired(value, 'Description', 1);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [field]: validation.error
    }));

    return validation.isValid;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Validate on change if field was already touched
    if (touched[field]) {
      validateField(field, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      phone: true,
      description: true
    });

    // Validate all fields
    const nameValid = validateField('name', formData.name);
    const emailValid = validateField('email', formData.email);
    const phoneValid = validateField('phone', formData.phone);
    const descValid = validateField('description', formData.description);

    if (!nameValid || !emailValid || !phoneValid || !descValid) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors in the form before submitting',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Fetch geolocation data
      const geoData = await getGeoLocation();
      
      // Extract numeric dial code from countryCode
      const dialCode = formData.countryCode.replace('+', '');
      
      // Build complete payload
      const payload = buildLeadPayload({
        name: formData.name,
        email: formData.email,
        countryDialCode: dialCode,
        phoneNumber: formData.phone,
        description: formData.description
      }, geoData);

      // Submit lead
      const result = await submitLead(payload);

      if (result.success && result.statusCode === 200) {
        toast({
          title: 'Success!',
          description: result.message || 'Your request has been submitted successfully.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          countryCode: '+1',
          description: ''
        });
        setErrors({});
        setTouched({});
        
        // Close modal
        onClose();
        
        // Redirect to application page with customerId
        if (result.customerId) {
          navigate(`/application/main?customerid=${result.customerId}`);
        }
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Something went wrong. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden">
        <div className="grid md:grid-cols-5">
          {/* Left Side - Image */}
          <div className="relative hidden md:block bg-navy-800 md:col-span-2 min-h-full">
            <img
              src="https://images.unsplash.com/photo-1660927604748-9b039cb31fc1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxsYXd5ZXIlMjBjbGllbnQlMjBjb25zdWx0YXRpb24lMjBkZXNrJTIwZG9jdW1lbnRzJTIwd2lkZXxlbnwwfHx8fDE3NzM5NTU1NzB8MA&ixlib=rb-4.1.0&q=85"
              alt="Professional signing legal documents"
              className="w-full h-full object-cover object-center absolute inset-0"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            <div className="absolute bottom-5 left-5 right-5 text-white">
              <h3 className="text-lg font-bold mb-1">Let's Get Started</h3>
              <p className="text-xs text-gray-200">
                Protect your brand with our expert services
              </p>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="relative p-5 bg-navy-700 flex flex-col md:col-span-3">
            <div className="mb-3 pr-8">
              <h2 className="text-lg font-bold text-gray-100 mb-0.5">Get in Touch</h2>
              <p className="text-xs text-gray-300">Fill out the form and we'll contact you shortly</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-2.5 flex-1 flex flex-col">
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-200 mb-1">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    onBlur={() => handleBlur('name')}
                    className={`pl-9 py-2 text-sm ${
                      errors.name && touched.name
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                        : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'
                    }`}
                  />
                </div>
                {errors.name && touched.name && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-200 mb-1">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    onBlur={() => handleBlur('email')}
                    className={`pl-9 py-2 text-sm ${
                      errors.email && touched.email
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                        : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'
                    }`}
                  />
                </div>
                {errors.email && touched.email && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Phone with Country Code */}
              <div>
                <label className="block text-xs font-semibold text-gray-200 mb-1">
                  Contact Number *
                </label>
                <div className="flex gap-2">
                  <Select value={formData.countryCode} onValueChange={(value) => handleChange('countryCode', value)}>
                    <SelectTrigger className="w-24 py-2 text-sm border-navy-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" side="top" className="z-[100] max-h-60 bg-navy-800 border-navy-600">
                      <SelectItem value="+1">+1 US</SelectItem>
                      <SelectItem value="+7">+7 RU</SelectItem>
                      <SelectItem value="+20">+20 EG</SelectItem>
                      <SelectItem value="+27">+27 ZA</SelectItem>
                      <SelectItem value="+30">+30 GR</SelectItem>
                      <SelectItem value="+31">+31 NL</SelectItem>
                      <SelectItem value="+32">+32 BE</SelectItem>
                      <SelectItem value="+33">+33 FR</SelectItem>
                      <SelectItem value="+34">+34 ES</SelectItem>
                      <SelectItem value="+36">+36 HU</SelectItem>
                      <SelectItem value="+39">+39 IT</SelectItem>
                      <SelectItem value="+40">+40 RO</SelectItem>
                      <SelectItem value="+41">+41 CH</SelectItem>
                      <SelectItem value="+43">+43 AT</SelectItem>
                      <SelectItem value="+44">+44 GB</SelectItem>
                      <SelectItem value="+45">+45 DK</SelectItem>
                      <SelectItem value="+46">+46 SE</SelectItem>
                      <SelectItem value="+47">+47 NO</SelectItem>
                      <SelectItem value="+48">+48 PL</SelectItem>
                      <SelectItem value="+49">+49 DE</SelectItem>
                      <SelectItem value="+51">+51 PE</SelectItem>
                      <SelectItem value="+52">+52 MX</SelectItem>
                      <SelectItem value="+53">+53 CU</SelectItem>
                      <SelectItem value="+54">+54 AR</SelectItem>
                      <SelectItem value="+55">+55 BR</SelectItem>
                      <SelectItem value="+56">+56 CL</SelectItem>
                      <SelectItem value="+57">+57 CO</SelectItem>
                      <SelectItem value="+58">+58 VE</SelectItem>
                      <SelectItem value="+60">+60 MY</SelectItem>
                      <SelectItem value="+61">+61 AU</SelectItem>
                      <SelectItem value="+62">+62 ID</SelectItem>
                      <SelectItem value="+63">+63 PH</SelectItem>
                      <SelectItem value="+64">+64 NZ</SelectItem>
                      <SelectItem value="+65">+65 SG</SelectItem>
                      <SelectItem value="+66">+66 TH</SelectItem>
                      <SelectItem value="+81">+81 JP</SelectItem>
                      <SelectItem value="+82">+82 KR</SelectItem>
                      <SelectItem value="+84">+84 VN</SelectItem>
                      <SelectItem value="+86">+86 CN</SelectItem>
                      <SelectItem value="+90">+90 TR</SelectItem>
                      <SelectItem value="+91">+91 IN</SelectItem>
                      <SelectItem value="+92">+92 PK</SelectItem>
                      <SelectItem value="+93">+93 AF</SelectItem>
                      <SelectItem value="+94">+94 LK</SelectItem>
                      <SelectItem value="+95">+95 MM</SelectItem>
                      <SelectItem value="+98">+98 IR</SelectItem>
                      <SelectItem value="+212">+212 MA</SelectItem>
                      <SelectItem value="+213">+213 DZ</SelectItem>
                      <SelectItem value="+216">+216 TN</SelectItem>
                      <SelectItem value="+218">+218 LY</SelectItem>
                      <SelectItem value="+220">+220 GM</SelectItem>
                      <SelectItem value="+221">+221 SN</SelectItem>
                      <SelectItem value="+233">+233 GH</SelectItem>
                      <SelectItem value="+234">+234 NG</SelectItem>
                      <SelectItem value="+249">+249 SD</SelectItem>
                      <SelectItem value="+254">+254 KE</SelectItem>
                      <SelectItem value="+255">+255 TZ</SelectItem>
                      <SelectItem value="+256">+256 UG</SelectItem>
                      <SelectItem value="+260">+260 ZM</SelectItem>
                      <SelectItem value="+263">+263 ZW</SelectItem>
                      <SelectItem value="+880">+880 BD</SelectItem>
                      <SelectItem value="+886">+886 TW</SelectItem>
                      <SelectItem value="+960">+960 MV</SelectItem>
                      <SelectItem value="+961">+961 LB</SelectItem>
                      <SelectItem value="+962">+962 JO</SelectItem>
                      <SelectItem value="+963">+963 SY</SelectItem>
                      <SelectItem value="+964">+964 IQ</SelectItem>
                      <SelectItem value="+965">+965 KW</SelectItem>
                      <SelectItem value="+966">+966 SA</SelectItem>
                      <SelectItem value="+967">+967 YE</SelectItem>
                      <SelectItem value="+968">+968 OM</SelectItem>
                      <SelectItem value="+971">+971 AE</SelectItem>
                      <SelectItem value="+972">+972 IL</SelectItem>
                      <SelectItem value="+973">+973 BH</SelectItem>
                      <SelectItem value="+974">+974 QA</SelectItem>
                      <SelectItem value="+975">+975 BT</SelectItem>
                      <SelectItem value="+976">+976 MN</SelectItem>
                      <SelectItem value="+977">+977 NP</SelectItem>
                      <SelectItem value="+993">+993 TM</SelectItem>
                      <SelectItem value="+994">+994 AZ</SelectItem>
                      <SelectItem value="+995">+995 GE</SelectItem>
                      <SelectItem value="+998">+998 UZ</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      type="tel"
                      placeholder="123-456-7890"
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        handleChange('phone', value);
                      }}
                      onBlur={() => handleBlur('phone')}
                      maxLength={10}
                      className={`pl-9 py-2 text-sm ${
                        errors.phone && touched.phone
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                          : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'
                      }`}
                    />
                  </div>
                </div>
                {errors.phone && touched.phone && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.phone}
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-200 mb-1">
                  Description *
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-2.5 text-gray-400" size={16} />
                  <Textarea
                    placeholder="Tell us about your needs..."
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    onBlur={() => handleBlur('description')}
                    className={`pl-9 pt-2.5 h-16 text-sm resize-none ${
                      errors.description && touched.description
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                        : 'border-navy-500 focus:border-brand-primary focus:ring-brand-primary'
                    }`}
                  />
                </div>
                {errors.description && touched.description && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.description}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-gradient text-white hover:opacity-90 py-2.5 text-sm font-bold shadow-lg mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </form>

            <p className="text-xs text-gray-400 text-center mt-2">
              By submitting, you agree to our Terms & Privacy Policy
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
